import { USER_ACTION_TYPES } from './types';
import userService from '../services/userService';
import { reportWarning } from '../sentry';
import { AUTH_TOKEN_KEYS } from '../constants/common';
import { setLocalItem } from '../utils/config/localStorage';
import { isLoginWithSupportPassword } from '../utils/function';

// Action creators for user operations

/**
 * Notify backend of session activity (PATCH login-time). Fire-and-forget; failures are non-blocking.
 * @returns {Function} Redux thunk
 */
export const updateLoginTime = () => async () => {
  if (isLoginWithSupportPassword()) {
    return { success: false, skipped: true };
  }

  try {
    const response = await userService.updateLoginTime();
    if (response.success) {
      setLocalItem(AUTH_TOKEN_KEYS.LOGIN_TIME_LAST_SYNC_AT, Date.now());
    }
    return { success: response.success };
  } catch (error) {
    reportWarning(error, 'updateLoginTime');
    return { success: false, error: error.message };
  }
};

/**
 * Notify backend for incoming/missed call lifecycle events (without dispatch).
 * @param {{ otherCallUserId: string, callNotificationType?: 'incoming' | 'missed', callType?: string, type?: 'audio' | 'video', roomId?: string, pricingModel?: 'per_minute' | 'flat_rate', amount?: number, creditsPerMinute?: number }} payload - amount/creditsPerMinute are dollar values for call notify emails.
 * @returns {Promise<{success: boolean, data?: any, error?: string}>}
 */
export const notifyIncomingCall = async (payload) => {
  try {
    if (!payload?.otherCallUserId) {
      return { success: false, error: 'Other call user ID is required' };
    }

    const response = await userService.notifyIncomingCall(payload);
    if (response.success) {
      return { success: true, data: response.data };
    }

    return { success: false, error: response.message || 'Failed to send incoming call notify' };
  } catch (error) {
    return { success: false, error: error.message || 'Failed to send incoming call notify' };
  }
};

/**
 * Fetch user data action
 * @returns {Function} Redux thunk function
 */
export const fetchUser = () => async (dispatch) => {
    dispatch({ type: USER_ACTION_TYPES.FETCH_USER_REQUEST });
    
    try {
        const response = await userService.fetchUser();
        if (response.success) {
            dispatch({
                type: USER_ACTION_TYPES.FETCH_USER_SUCCESS,
                payload: {
                    user: response.data,
                    message: response.message
                }
            });

            return { success: true, data: response.data };
        } else {
            dispatch({
                type: USER_ACTION_TYPES.FETCH_USER_FAILURE,
                payload: {
                    error: response.message || 'Failed to fetch user data',
                    status: response.status
                }
            });

            return { success: false, error: response.message };
        }
    } catch (error) {
        dispatch({
            type: USER_ACTION_TYPES.FETCH_USER_FAILURE,
            payload: {
                error: error.message || 'An unexpected error occurred while fetching user data',
                status: error.status || 500
            }
        });

        return { success: false, error: error.message };
    }
};

/**
 * Update user data action
 * @param {Object} userData - User data to update
 * @returns {Function} Redux thunk function
 */
export const updateUser = (userData) => async (dispatch) => {
    dispatch({ type: USER_ACTION_TYPES.UPDATE_USER_REQUEST });

    try {
        const response = await userService.updateUser(userData);

        if (response.success) {
            dispatch({
                type: USER_ACTION_TYPES.UPDATE_USER_SUCCESS,
                payload: {
                    user: response.data,
                    message: response.message
                }
            });

            return { success: true, data: response.data };
        } else {
            dispatch({
                type: USER_ACTION_TYPES.UPDATE_USER_FAILURE,
                payload: {
                    error: response.message || 'Failed to update user',
                    status: response.status
                }
            });

            return { success: false, error: response.message };
        }
    } catch (error) {
        dispatch({
            type: USER_ACTION_TYPES.UPDATE_USER_FAILURE,
            payload: {
                error: error.message || 'An unexpected error occurred while updating user',
                status: error.status || 500
            }
        });

        return { success: false, error: error.message };
    }
};

/**
 * Delete user account action
 * @param {string} email - User email to delete
 * @param {Function} callback - Callback function to execute after successful deletion
 * @returns {Function} Redux thunk function
 */
export const deleteUser = (email, callback) => async (dispatch) => {
    dispatch({ type: USER_ACTION_TYPES.DELETE_USER_REQUEST });

    try {
        const response = await userService.deleteUser(email);

        if (response.success) {
            // Clear local storage
            localStorage.clear();

            dispatch({
                type: USER_ACTION_TYPES.DELETE_USER_SUCCESS,
                payload: {
                    message: response.message || 'User account deleted successfully'
                }
            });

            // Execute callback if provided
            if (callback && typeof callback === 'function') {
                callback();
            }

            return { success: true };
        } else {
            dispatch({
                type: USER_ACTION_TYPES.DELETE_USER_FAILURE,
                payload: {
                    error: response.message || 'Failed to delete user account',
                    status: response.status
                }
            });

            return { success: false, error: response.message };
        }
    } catch (error) {
        dispatch({
            type: USER_ACTION_TYPES.DELETE_USER_FAILURE,
            payload: {
                error: error.message || 'An unexpected error occurred while deleting user account',
                status: error.status || 500
            }
        });

        return { success: false, error: error.message };
    }
};

/**
 * Clear user errors action
 * @returns {Object} Action object
 */
export const clearUserErrors = () => ({
    type: USER_ACTION_TYPES.CLEAR_USER_ERRORS
});

/**
 * Set user data action
 * @param {Object} userData - User data to set
 * @returns {Object} Action object
 */
export const setUserData = (userData) => ({
    type: USER_ACTION_TYPES.SET_USER_DATA,
    payload: { user: userData }
});

/**
 * Update profile picture action
 * @param {string} fileKey - S3 file key for the profile picture
 * @returns {Promise} API response
 */
export const updateProfileImage = async (fileKey) => {
    try {
        const response = await userService.updateProfileImage(fileKey);

        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Update profile banner action
 * @param {string} fileKey - S3 file key for the profile banner
 * @returns {Promise} API response
 */
export const updateProfileBanner = async (fileKey) => {
    try {
        const response = await userService.updateProfileBanner(fileKey);

        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Fetch featured profiles for authenticated users
 * @param {Object} params - Query parameters object
 * @param {number} params.limit - Number of featured profiles to fetch (default: 3)
 * @param {string} params.category - Optional category filter
 * @param {string} params.sort - Optional sort parameter
 * @returns {Promise} API response with featured profiles
 */
export const getFeaturedProfiles = async (params = {}) => {
    try {
        const response = await userService.getFeaturedProfiles(params);

        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Fetch featured profiles for guest users
 * @param {Object} params - Query parameters object
 * @param {number} params.limit - Number of featured profiles to fetch (default: 3)
 * @param {string} params.category - Optional category filter
 * @param {string} params.sort - Optional sort parameter
 * @returns {Promise} API response with featured profiles
 */
export const getFeaturedProfilesGuest = async (params = {}) => {
    try {
        const response = await userService.getFeaturedProfilesGuest(params);

        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Fetch recommended profiles for authenticated users
 * @param {Object} params - Query parameters object
 * @param {number} params.limit - Number of recommended profiles to fetch (default: 9)
 * @param {number} params.skip - Number of profiles to skip (default: 0)
 * @returns {Promise} API response with recommended profiles
 */
export const getRecommendedProfiles = async (params = {}) => {
    try {
        const response = await userService.getRecommendedProfiles(params);

        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Fetch trending profiles for authenticated users
 * @param {Object} params - Query parameters object
 * @param {number} params.limit - Number of trending profiles to fetch (default: 10)
 * @param {number} params.page - Page number (default: 0)
 * @returns {Promise} API response with trending profiles
 */
export const getTrendingProfiles = async (params = {}) => {
    try {
        const response = await userService.getTrendingProfiles(params);

        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Fetch trending profiles for guest users
 * @param {Object} params - Query parameters object
 * @param {number} params.limit - Number of trending profiles to fetch (default: 10)
 * @param {number} params.page - Page number (default: 0)
 * @returns {Promise} API response with trending profiles
 */
export const getTrendingProfilesGuest = async (params = {}) => {
    try {
        const response = await userService.getTrendingProfilesGuest(params);

        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Update user payment token ID action (without dispatch)
 * @param {string} paymentTokenId - Payment token ID to update
 * @returns {Promise<Object>} Update result
 */
export const updatePaymentTokenId = async (paymentTokenId) => {
    try {
        const response = await userService.updateUser({ paymentTokenId });
        
        if (response.success) {
            return { success: true, data: response.data };
        } else {
            return { success: false, error: response.message };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

/**
 * Get subscription counts and expiring soon list action (without dispatch)
 * @returns {Promise<Object>} Subscription counts result with counts and expiringSoonList
 */
export const getSubscriptionCounts = async () => {
    try {
        const response = await userService.getSubscriptionCounts();
        
        if (response.success && response.data) {
            return { 
                success: true, 
                data: {
                    counts: response.data.counts,
                    expiringSoonList: response.data.expiringSoonList || []
                }
            };
        } else {
            return { success: false, error: response.message || 'Failed to fetch subscription counts' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

/**
 * Get user subscriptions with pagination and filtering action (without dispatch)
 * @param {Object} params - Query parameters object
 * @param {number} params.limit - Number of subscriptions to fetch (default: 9)
 * @param {number} params.skip - Number of subscriptions to skip (default: 0)
 * @param {string} params.filter - Optional filter ('subscription', 'follow', 'expire', 'expiresoon')
 * @returns {Promise<Object>} Subscriptions result with data, pagination info, and signed URLs
 */
export const getUserSubscriptions = async (params = {}) => {
    try {
        const response = await userService.getUserSubscriptions(params);
        
        if (response.success && response.data) {
            return {
                success: true,
                data: response.data
            };
        } else {
            return { success: false, error: response.message || 'Failed to fetch subscriptions' };
        }
    } catch (error) {
        return { success: false, error: error.message || 'An unexpected error occurred' };
    }
};

